import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCumulativeSwitchComponent from 'webapp/components/form/cumulative-switch';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import MetricDetailsExploreDataAnomalyDetectionAnomalySwitchComponent from 'webapp/components/metric-details/explore/data/anomaly-detection/anomaly-switch';
import type { AnomalyConfiguration } from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';
import MetricDetailsSettingsFilterSectionComponent from 'webapp/components/metric-details/settings/filter-section';

import type { MetricSetup } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import type DateRange from 'webapp/models-custom/date-range';

interface Args {
	metricSetup: MetricSetup;
	blockM: Block | null;
	dateRange?: DateRange | null;
	isDisabled?: boolean;
	origin?: string;

	// Anomaly state (owned by parent for URL serialization)
	anomalyConfiguration?: AnomalyConfiguration;
	isAnomalySwitchChecked?: boolean;
	onChangeAnomalySwitchState?: (newValue: boolean) => void;
	onChangeAnomalyConfiguration?: (configuration: AnomalyConfiguration) => void;
	onChangeCumulative?: (isCumulative: boolean) => void;
}

export default class MetricDetailsSettingsVisualizeComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;

	get limitations() {
		const blockSetup = this.args.blockM?.blockSetup;
		return blockSetup?.firstShownDataSelect?.limitations ?? blockSetup?.limitations ?? null;
	}

	get showCumulative(): boolean {
		return (
			!!this.args.onChangeCumulative &&
			!!this.args.blockM?.blockSetup?.visualization?.showCumulative
		);
	}

	get showAnomalySwitch(): boolean {
		return (
			!!this.args.onChangeAnomalySwitchState &&
			!!this.args.onChangeAnomalyConfiguration &&
			(this.args.blockM?.visualization === BlockVisualization.LINE_CHART ||
				this.args.blockM?.visualization === BlockVisualization.BAR_CHART)
		);
	}

	get changeCumulative() {
		return this.args.onChangeCumulative!;
	}

	get changeAnomalySwitchState() {
		return this.args.onChangeAnomalySwitchState!;
	}

	get changeAnomalyConfiguration() {
		return this.args.onChangeAnomalyConfiguration!;
	}

	get isAnomalySwitchChecked() {
		return this.args.isAnomalySwitchChecked ?? false;
	}

	get anomalyConfiguration() {
		return this.args.anomalyConfiguration!;
	}

	get dateRange() {
		return this.args.dateRange ?? null;
	}

	get allowMultipleDimensions() {
		return (
			!!this.args.metricSetup?.allowDimensions &&
			!!this.args.blockM?.blockSetup?.firstShownDataSelect?.allowAdvancedSetup
		);
	}

	get anomalyStatusObjectInErrorOrInfo() {
		const anomalyDataSelect = this.args.blockM?.blockSetup?.dataSelects.find(
			(ds) => ds.isAnomaly,
		);
		return anomalyDataSelect?.statusObjects.find((so) => so.isError || so.isInfo);
	}

	get showOptionsSection(): boolean {
		return this.showCumulative || this.showAnomalySwitch;
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.args.metricSetup.dimensions = dimensions;
		this.args.metricSetup.resetFilters();

		if (this.args.origin) {
			const metric = this.args.metricSetup.metric;
			this.analyticsService.event(TrackingEvent.METRIC_DETAILS_BREAKDOWN_BY, {
				origin: this.args.origin,
				datasource: metric?.dataSourceTypeKey,
				metrics: metric?.name,
				dimension: dimensions[0] ?? null,
			});
		}
	}

	<template>
		{{#if this.showOptionsSection}}
			<div class="metric-explorer-settings__section">
				<div class="metric-explorer-settings__section-header">
					<CommonUiIconComponent @name="ChartLine" @size="sm" />
					Options
				</div>
				{{#if this.showCumulative}}
					{{#if this.limitations}}
						<FormCumulativeSwitchComponent
							@metricLimitations={{this.limitations}}
							@onChange={{this.changeCumulative}}
							@size="small"
						/>
					{{/if}}
				{{/if}}
				{{#if this.showAnomalySwitch}}
					<MetricDetailsExploreDataAnomalyDetectionAnomalySwitchComponent
						@changeAnomalySwitchState={{this.changeAnomalySwitchState}}
						@changeAnomalyConfiguration={{this.changeAnomalyConfiguration}}
						@anomalyStatusObjectInErrorOrInfo={{this.anomalyStatusObjectInErrorOrInfo}}
						@isAnomalySwitchChecked={{this.isAnomalySwitchChecked}}
						@anomalyConfiguration={{this.anomalyConfiguration}}
						@metricSetup={{@metricSetup}}
						@dateRange={{this.dateRange}}
					/>
				{{/if}}
			</div>
		{{/if}}

		{{#if this.showOptionsSection}}
			<hr class="metric-explorer-settings__section-divider" />
		{{/if}}

		<div class="metric-explorer-settings__section">
			<div class="metric-explorer-settings__section-header">
				<CommonUiIconComponent @name="Breakdown" @size="sm" />
				Breakdown by
			</div>
			<FormSelectDimensionsComponent
				@metricSetup={{@metricSetup}}
				@onChange={{this.changeDimensions}}
				@isDisabled={{@isDisabled}}
				@showFloatlabel={{false}}
				@placeholder="Select dimension"
				@isMultiple={{this.allowMultipleDimensions}}
			/>
		</div>

		<MetricDetailsSettingsFilterSectionComponent
			@metricSetup={{@metricSetup}}
			@blockM={{@blockM}}
			@dateRange={{@dateRange}}
			@isDisabled={{@isDisabled}}
			@trackingOrigin={{@origin}}
		/>
	</template>
}
